/**
 * Utility function to calculate the average score from a collection of score objects.
 *
 * @param scores Array of score objects (expects the structure { value: number }).
 * @returns The average score value (0-10), or null if the input array is empty.
 */
export function calculateAverageScore(scores: Array<{ value: number }>): number | null {
    if (!scores || scores.length === 0) {
        return null;
    }

    const sum = scores.reduce((acc, score) => acc + score.value, 0);
    const average = sum / scores.length;

    // Ensure the average is within the 0-10 range and formatted to one decimal place
    return parseFloat(Math.max(0, Math.min(10, average)).toFixed(1));
}

/**
 * Gets the appropriate color scheme based on a score value
 * @param score The score value (0-10)
 * @returns 'red', 'orange', or 'green' based on the score value
 */
export function getColorSchemeForScore(score: number): 'red' | 'orange' | 'green' {
    if (score < 4) {
        return 'red';
    } else if (score > 7) {
        return 'green';
    } else {
        return 'orange';
    }
}

/**
 * Analyzes a set of scores and determines a textual summary classification
 * reflecting the overall quality and consistency.
 *
 * @param scores An array of score objects, each containing a kind and value (0-10).
 * @returns A descriptive summary string reflecting the aggregate score profile.
 */
export function getScoreSummaryText(
    scores: Array<{
        kind: string;
        value: number;
    }> | null
): string {
    if (!scores || scores.length === 0) {
        return 'Score unavailable';
    }

    const averageScore = calculateAverageScore(scores);
    if (averageScore === null) {
        // Defensive check
        return 'Score calculation error';
    }

    // Define thresholds
    const exceptionalThreshold = 9.5;
    const highThreshold = 8.0;
    const midThreshold = 6.0;
    const lowThreshold = 4.0;

    // Generate summary based *only* on the average score
    if (averageScore >= exceptionalThreshold) {
        return 'Excellent relevance and highly informative';
    }
    if (averageScore >= highThreshold) {
        return 'Highly relevant and informative content';
    }
    if (averageScore >= midThreshold) {
        return 'Generally relevant and informative';
    }
    if (averageScore >= lowThreshold) {
        return 'Moderately relevant or lacks depth/informativeness';
    }

    // Average score is below lowThreshold
    return 'Suggests low relevance or lacks informative value';
}

/**
 * Maps a numeric score value (0-10) to a qualitative level string.
 */
export function getScoreLevelText(value: number): string {
    if (value >= 9.5) return 'Excellent';
    if (value >= 8.0) return 'Great';
    if (value > 7.0) return 'Good';
    if (value >= 4.0) return 'Moderate';
    return 'Poor';
}
